import { useState, useEffect } from 'react';
import { NewsSource } from '../types';
import {
  IconPlus,
  IconRefresh,
  IconPlayerPause,
  IconPlayerPlay,
  IconPencil,
  IconTrash,
  IconStar,
} from '@tabler/icons-react';
import {
  listSources,
  createSource,
  updateSource,
  deleteSource,
  toggleSourceActive,
  triggerNewsProcessing,
} from '../services/rssService';
import SourceModal from '../components/SourceModal';

export default function NewsSourcesPage() {
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSource, setEditingSource] = useState<NewsSource | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSources();
  }, [searchQuery]);

  const loadData = async () => {
    try {
      await loadSources();
    } finally {
      setLoading(false);
    }
  };

  const loadSources = async () => {
    try {
      const result = await listSources({
        searchQuery: searchQuery || undefined,
      });
      setSources(result.sources);
    } catch (error) {
      console.error('Error loading sources:', error);
    }
  };

  const handleAdd = () => {
    setEditingSource(null);
    setShowModal(true);
  };

  const handleEdit = (source: NewsSource) => {
    setEditingSource(source);
    setShowModal(true);
  };

  const handleDelete = async (source: NewsSource) => {
    if (!confirm(`Are you sure you want to delete "${source.name}"?`)) {
      return;
    }

    try {
      await deleteSource(source.id);
      await loadSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      alert('Failed to delete source');
    }
  };

  const handleToggleActive = async (source: NewsSource) => {
    try {
      await toggleSourceActive(source.id, !source.isActive);
      await loadSources();
    } catch (error) {
      console.error('Error toggling source:', error);
      alert('Failed to toggle source status');
    }
  };

  const handleSave = async (sourceData: Omit<NewsSource, 'id' | 'lastFetchedAt'>) => {
    try {
      if (editingSource) {
        await updateSource(editingSource.id, sourceData);
      } else {
        await createSource(sourceData);
      }
      setShowModal(false);
      await loadSources();
    } catch (error) {
      console.error('Error saving source:', error);
      throw error;
    }
  };

  const handleTriggerProcessing = async () => {
    if (!confirm('Trigger news feed processing now?')) {
      return;
    }

    setProcessing(true);
    try {
      const result = await triggerNewsProcessing();
      alert(result.message || 'News processing triggered successfully');
    } catch (error) {
      console.error('Error triggering news processing:', error);
      alert('Failed to trigger news processing');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">News</div>
          <h2 className="page-title">Sources</h2>
        </div>
        <div className="col-auto">
          <div className="btn-list">
            <button
              onClick={handleTriggerProcessing}
              className="btn btn-outline-primary"
              disabled={processing}
            >
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                <>
                  <IconRefresh className="icon" />
                  Trigger Processing
                </>
              )}
            </button>
            <button onClick={handleAdd} className="btn btn-primary">
              <IconPlus className="icon" />
              Add Source
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="row g-2 mt-3">
        <div className="col-md-12">
          <input
            type="text"
            className="form-control"
            placeholder="Search sources..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Sources Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>URL</th>
                <th>Interval (min)</th>
                <th>Word Limit</th>
                <th>Status</th>
                <th>Last Fetched</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sources.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No sources found
                  </td>
                </tr>
              ) : (
                sources.map(source => (
                  <tr key={source.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        {source.imageUrl && (
                          <img
                            src={source.imageUrl}
                            alt={source.name}
                            className="avatar avatar-sm me-2"
                          />
                        )}
                        <span className="text-reset">{source.name}</span>
                        {source.promoted && (
                          <IconStar className="icon text-yellow ms-2" fill="currentColor" />
                        )}
                      </div>
                    </td>
                    <td>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-reset"
                      >
                        {source.url.length > 50 ? source.url.substring(0, 50) + '...' : source.url}
                      </a>
                    </td>
                    <td>{source.fetchInterval}</td>
                    <td>{source.contentWordLimit || '–'}</td>
                    <td>
                      <span
                        className={`badge text-white ${source.isActive ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {source.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {source.lastFetchedAt?.toDate
                        ? new Date(source.lastFetchedAt.toDate()).toLocaleString()
                        : 'Never'}
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => handleToggleActive(source)}
                          className="btn btn-icon"
                          title={source.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {source.isActive ? (
                            <IconPlayerPause className="icon" />
                          ) : (
                            <IconPlayerPlay className="icon" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(source)}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(source)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <SourceModal
          source={editingSource}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
